import { useState } from 'react'
import { Link } from 'react-router-dom'
import Card from '@/components/common/Card'
import Badge from '@/components/common/Badge'
import Button from '@/components/common/Button'
import ContactScrimmageModal from './ContactScrimmageModal'
import AutoPostingBadge from './AutoPostingBadge'
import MatchTierIndicator from './MatchTierIndicator'
import { HOCKEY_AGE_GROUPS } from '@/lib/hockeyData'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'

export default function ScrimmageCard({ result }) {
  const [showContact, setShowContact] = useState(false)
  const [showReportMenu, setShowReportMenu] = useState(false)
  const [reportLoading, setReportLoading] = useState(false)
  
  const { 
    posting, 
    team_info, 
    manager_info, 
    distance_miles, 
    has_conflict, 
    conflict_type, 
    conflicting_events,
    match_tier,
    match_score,
    match_reason,
    // Combined score fields from precomputed matches
    combined_score,
    solo_confidence_score,
    schedule_fit_score,
    distance_score,
    // Perspective for precomputed matches (supply view has no contact info)
    match_perspective,
    // Penalty breakdown
    penalty_applied,
    same_association,
    same_district,
    posting_district,
    demand_district,
    // Calendar events for this date (real-time scoring mode)
    your_calendar_events,  // Demand view: YOUR calendar
    their_calendar_events, // Supply view: THEIR calendar (other team)
    other_team_name        // Supply view: name of other team for label
  } = result

  const formatDate = (date) => {
    // Parse date string as local date to avoid timezone shift
    // Database stores DATE type (e.g., "2025-10-31")
    const [year, month, day] = date.split('-').map(Number)
    const localDate = new Date(year, month - 1, day)
    
    return localDate.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    })
  }

  const formatTime = (time) => {
    if (!time) return ''
    const [hours, minutes] = time.split(':')
    const hour = parseInt(hours)
    const ampm = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour % 12 || 12
    return `${displayHour}:${minutes} ${ampm}`
  }

  // Find and display age group label
  const ageGroupLabel = HOCKEY_AGE_GROUPS.find(
    ag => ag.value === posting.team_level
  )?.label || posting.team_level
  
  const levelLabel = posting.level_detail ? `${ageGroupLabel} ${posting.level_detail}` : ageGroupLabel

  // Use display_name directly (already formatted in database)
  // Format: "[City] Squirt B1 [Color]" e.g. "Andover Squirt B1 Gold"
  const displayTeamName = team_info.name || 'Unknown Team'
  
  // Determine if this is an Away or Home opportunity for the viewing user
  const isAwayOpportunity = match_perspective === 'demand' // You go TO their rink
  const isHomeOpportunity = match_perspective === 'supply' // They come TO your rink
  
  // Calendar conflict badge
  const renderConflictBadge = () => {
    if (has_conflict === undefined || has_conflict === null) return null
    
    if (!has_conflict) {
      return (
        <div className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-medium">
          <span>🟢</span>
          <span>Your team is free</span>
        </div>
      )
    }
    
    if (conflict_type === 'time_overlap') {
      const firstConflict = conflicting_events?.[0]
      const conflictTitle = firstConflict?.title || 'Event'
      const conflictTime = firstConflict?.start_time ? new Date(firstConflict.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : ''
      
      return (
        <div className="inline-flex items-center gap-1 px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-medium" title={`Conflict: ${conflictTitle} ${conflictTime}`}>
          <span>🟡</span>
          <span>Possible overlap</span>
        </div>
      )
    }
    
    if (conflict_type === 'same_day') {
      const firstConflict = conflicting_events?.[0]
      const conflictTitle = firstConflict?.title || 'Event'
      const conflictTime = firstConflict?.start_time ? new Date(firstConflict.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : ''
      
      return (
        <div className="inline-flex items-center gap-1 px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-medium" title={`Conflict: ${conflictTitle} ${conflictTime}`}>
          <span>🔴</span>
          <span>Conflict: {conflictTitle} {conflictTime}</span>
        </div>
      )
    }
    
    return null
  }

  const contactEmail = manager_info?.email
  const contactPhone = manager_info?.phone
  const isAutoGenerated = posting.auto_generated
  const isSeedTeam = team_info.data_source === 'seed_auto'
  // Supply perspective means we're showing teams interested in OUR ice - no contact available
  const isSupplyPerspective = match_perspective === 'supply'

  const handleReport = async (reason) => {
    setReportLoading(true)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      
      await supabase.from('posting_reports').insert({
        posting_id: posting.id,
        reporter_user_id: session?.user?.id || null,
        reporter_email: session?.user?.email || null,
        reason: reason
      })
      
      toast.success('Report submitted. Thank you for helping keep our listings accurate!')
      setShowReportMenu(false)
    } catch (error) {
      console.error('Report error:', error)
      toast.error('Failed to submit report')
    } finally {
      setReportLoading(false)
    }
  }

  const handleContact = async () => {
    // Supply perspective: these are teams interested in your ice, contact not available
    if (isSupplyPerspective) {
      toast.info('This team might be interested in your ice. They\'ll see your posting and can contact you directly.')
      return
    }
    
    // Handle seed teams without managers - log interest but show info toast
    if (isSeedTeam && !manager_info?.email && !manager_info?.phone) {
      // Log interest for unclaimed teams (helps understand demand)
      try {
        const { data: { session } } = await import('@/lib/supabase').then(m => m.supabase.auth.getSession())
        await fetch(
          `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/log-posting-contact`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(session?.access_token && { 'Authorization': `Bearer ${session.access_token}` }),
              'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY
            },
            body: JSON.stringify({
              postingId: posting.id,
              contactMethod: 'interest_only',
              isUnclaimedTeam: true
            })
          }
        )
      } catch {
        // Silent fail for interest logging
      }
      toast.info('This team is not yet on the platform. Sign up to manage your team\'s postings and respond to inquiries.')
      return
    }
    
    if (contactEmail || contactPhone) {
      setShowContact(true)
      return
    }
    toast.info('Contact details are hidden for this posting. Try another scrimmage or check back later.')
  }

  return (
    <>
      <Card className="hover:shadow-card-hover transition-shadow">
        {/* Desktop: Horizontal Layout */}
        <div className="hidden lg:flex items-start justify-between">
          <div className="flex-1">
            {/* Auto-Generated Badge with Confidence Tooltip */}
            {isAutoGenerated && posting.confidence_score && (
              <div className="mb-2">
                <AutoPostingBadge 
                  confidenceScore={posting.confidence_score} 
                  confidenceReasons={posting.confidence_reasons}
                />
              </div>
            )}

            {/* Match Tier Indicator (supports both tier-based and combined score) */}
            {(match_tier !== undefined && match_tier !== null) || (combined_score !== undefined && combined_score !== null) ? (
              <div className="mb-3">
                <MatchTierIndicator
                  matchTier={match_tier}
                  matchScore={match_score}
                  matchReason={match_reason}
                  combinedScore={combined_score}
                  soloConfidence={solo_confidence_score}
                  scheduleFit={schedule_fit_score}
                  distanceScore={distance_score}
                  distanceMiles={distance_miles}
                />
              </div>
            ) : null}

            {/* Calendar Conflict Badge */}
            {(has_conflict !== undefined && has_conflict !== null) && !match_tier && !combined_score && (
              <div className="mb-2">
                {renderConflictBadge()}
              </div>
            )}
            
            {/* Team Name & Level */}
            <div className="mb-3">
              <div className="flex items-center gap-2 mb-1">
                {/* Away/Home opportunity badge for precomputed matches */}
                {(isAwayOpportunity || isHomeOpportunity) && (
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold ${
                    isAwayOpportunity 
                      ? 'bg-blue-100 text-blue-800' 
                      : 'bg-amber-100 text-amber-800'
                  }`}>
                    {isAwayOpportunity ? '✈️ AWAY' : '🏠 HOME'}
                  </span>
                )}
                <h3 className="font-heading text-lg font-bold text-brand-charcoal">
                  {displayTeamName}
                </h3>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {/* Only show posting type badge when NOT in precomputed match view */}
                {!isAwayOpportunity && !isHomeOpportunity && (
                  <Badge variant={posting.posting_type === 'available' ? 'success' : 'info'}>
                    {posting.posting_type === 'available' ? 'Home Ice Available' : 'Seeking Away Game'}
                  </Badge>
                )}
                <Badge variant="default">
                  {levelLabel}
                </Badge>
                {posting.prefer_non_district && (
                  <Badge variant="info">Non-District Preferred</Badge>
                )}
                {posting.home_and_home && (
                  <Badge variant="info">Home & Home</Badge>
                )}
                {distance_miles && (
                  <Badge variant="default">
                    {Math.round(parseFloat(distance_miles))} mi
                  </Badge>
                )}
              </div>
            </div>

            {/* Date & Time */}
            <p className="text-brand-charcoal font-medium mb-2">
              {formatDate(posting.date)} • {formatTime(posting.start_time)}
              {posting.end_time && ` - ${formatTime(posting.end_time)}`}
            </p>

            {/* Location (for available postings) */}
            {posting.posting_type === 'available' && (
              <p className="text-brand-slate text-sm mb-2">
                {posting.rink_name && `${posting.rink_name} • `}
                {posting.city}, {posting.state}
                {posting.rink_address && ` • ${posting.rink_address}`}
              </p>
            )}

            {/* Wanted location hint */}
            {posting.posting_type === 'wanted' && (
              <p className="text-brand-slate text-sm mb-2">
                Looking for scrimmage near {posting.city}, {posting.state}
              </p>
            )}

            {/* Referee Info */}
            {posting.referee_type && posting.referee_type !== 'none' && (
              <p className="text-brand-slate text-sm">
                {posting.referee_type === 'coach_led' ? '👥 Coach-led scrimmage' : '👔 Paid referees'}
                {posting.scrimmage_fee && ` - $${posting.scrimmage_fee} split`}
              </p>
            )}

            {/* Notes */}
            {posting.notes && (
              <p className="text-brand-slate text-sm mt-2 italic">
                "{posting.notes}"
              </p>
            )}

            {/* Penalty Details (for debugging/transparency) */}
            {penalty_applied > 0 && (
              <div className="mt-2 flex gap-2 flex-wrap">
                {same_association && (
                  <span className="text-xs px-2 py-0.5 bg-red-100 text-red-700 rounded">
                    Same Association (-25)
                  </span>
                )}
                {same_district && !same_association && (
                  <span className="text-xs px-2 py-0.5 bg-orange-100 text-orange-700 rounded">
                    Same District: {demand_district} (-20)
                  </span>
                )}
              </div>
            )}
            {!penalty_applied && !same_association && !same_district && posting_district && demand_district && posting_district !== demand_district && (
              <div className="mt-2">
                <span className="text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded">
                  Different District: {posting_district} → {demand_district}
                </span>
              </div>
            )}

            {/* Calendar Events for this date - show YOUR or THEIR schedule depending on perspective */}
            {(your_calendar_events !== undefined || their_calendar_events !== undefined) && (
              <div className="mt-3 p-2 bg-gray-50 rounded border">
                <div className="text-xs font-medium text-gray-700 mb-1">
                  📋 {match_perspective === 'supply' 
                    ? `${other_team_name || 'Their'}'s Schedule` 
                    : 'Your Schedule'} on {formatDate(posting.date)}:
                </div>
                {(() => {
                  const events = match_perspective === 'supply' ? their_calendar_events : your_calendar_events
                  return events && events.length > 0 ? (
                    <div className="space-y-1">
                      {events.map((evt, idx) => (
                        <div key={idx} className="text-xs flex items-center gap-2">
                          <span className={`px-1.5 py-0.5 rounded ${
                            evt.event_type === 'game' ? 'bg-red-100 text-red-700' :
                            evt.event_type === 'practice' ? 'bg-blue-100 text-blue-700' :
                            evt.event_type === 'tournament' ? 'bg-purple-100 text-purple-700' :
                            'bg-gray-100 text-gray-700'
                          }`}>
                            {evt.event_type}
                          </span>
                          <span className="text-gray-600">
                            {new Date(evt.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                            {evt.end_time && ` - ${new Date(evt.end_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`}
                          </span>
                          <span className="text-gray-500 truncate">{evt.title}</span>
                          {evt.location && <span className="text-gray-400 truncate">@ {evt.location}</span>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-xs text-gray-500 italic">
                      No calendar events synced for this date
                    </div>
                  )
                })()}
              </div>
            )}

            {/* Association Website Link for seed teams */}
            {isSeedTeam && team_info.association_website_url && (
              <a
                href={team_info.association_website_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 mt-2 text-xs text-brand-slate hover:text-brand-charcoal"
              >
                <span>🔗</span>
                <span>Visit {team_info.association_name || 'Association'} Website</span>
              </a>
            )}

            {/* Claim Team CTA for unclaimed seed teams */}
            {isSeedTeam && !team_info.claimed_at && (
              <Link 
                to={`/claim-team/${posting.team_id}`}
                className="inline-flex items-center gap-1.5 mt-2 text-xs text-brand-green hover:text-green-700 hover:underline"
              >
                <span>🏒</span>
                <span>Is this your team? Claim it to manage postings</span>
              </Link>
            )}

            {/* Report Posting */}
            <div className="relative inline-block mt-2">
              <button
                onClick={() => setShowReportMenu(!showReportMenu)}
                className="text-xs text-brand-slate hover:text-brand-charcoal"
              >
                ⚑ Report
              </button>
              {showReportMenu && (
                <div className="absolute z-50 left-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg text-xs">
                  <p className="px-3 py-2 font-medium text-brand-charcoal border-b">Report as:</p>
                  {['inaccurate', 'outdated', 'spam', 'other'].map((reason) => (
                    <button
                      key={reason}
                      onClick={() => handleReport(reason)}
                      disabled={reportLoading}
                      className="block w-full px-3 py-2 text-left hover:bg-gray-50 capitalize"
                    >
                      {reason}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Contact Button */}
          <div className="ml-4">
            <Button
              variant={isSupplyPerspective ? "secondary" : "primary"}
              size="sm"
              onClick={handleContact}
            >
              {isSupplyPerspective ? 'View Match' : 'Contact'}
            </Button>
          </div>
        </div>

        {/* Mobile: Optimized Vertical Layout */}
        <div className="lg:hidden">
          {/* Auto-Generated Badge with Confidence Tooltip */}
          {isAutoGenerated && posting.confidence_score && (
            <div className="mb-2">
              <AutoPostingBadge 
                confidenceScore={posting.confidence_score} 
                confidenceReasons={posting.confidence_reasons}
              />
            </div>
          )}

          {/* Match Tier Indicator (supports both tier-based and combined score) */}
          {(match_tier !== undefined && match_tier !== null) || (combined_score !== undefined && combined_score !== null) ? (
            <div className="mb-3">
              <MatchTierIndicator
                matchTier={match_tier}
                matchScore={match_score}
                matchReason={match_reason}
                combinedScore={combined_score}
                soloConfidence={solo_confidence_score}
                scheduleFit={schedule_fit_score}
                distanceScore={distance_score}
                distanceMiles={distance_miles}
              />
            </div>
          ) : null}

          {/* Calendar Conflict Badge */}
          {(has_conflict !== undefined && has_conflict !== null) && !match_tier && !combined_score && (
            <div className="mb-2">
              {renderConflictBadge()}
            </div>
          )}
          
          {/* Header: Team Name + Contact Button */}
          <div className="flex items-start justify-between gap-3 mb-2">
            <div className="flex-1">
              {/* Away/Home badge for precomputed matches */}
              {(isAwayOpportunity || isHomeOpportunity) && (
                <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-bold mb-1 ${
                  isAwayOpportunity 
                    ? 'bg-blue-100 text-blue-800' 
                    : 'bg-amber-100 text-amber-800'
                }`}>
                  {isAwayOpportunity ? '✈️ AWAY' : '🏠 HOME'}
                </span>
              )}
              <h3 className="font-heading text-base font-bold text-brand-charcoal leading-tight">
                {displayTeamName}
              </h3>
            </div>
            <Button
              variant={isSupplyPerspective ? "secondary" : "primary"}
              size="sm"
              onClick={handleContact}
              className="shrink-0"
            >
              {isSupplyPerspective ? 'View Match' : 'Contact'}
            </Button>
          </div>

          {/* Primary Badges: Type + Level */}
          <div className="flex items-center gap-1.5 flex-wrap mb-2">
            {/* Only show posting type badge when NOT in precomputed match view */}
            {!isAwayOpportunity && !isHomeOpportunity && (
              <Badge variant={posting.posting_type === 'available' ? 'success' : 'info'} className="text-xs">
                {posting.posting_type === 'available' ? 'Home Ice Available' : 'Seeking Away Game'}
              </Badge>
            )}
            <Badge variant="default" className="text-xs">
              {levelLabel}
            </Badge>
            {posting.prefer_non_district && (
              <Badge variant="info" className="text-xs">Non-District Preferred</Badge>
            )}
            {posting.home_and_home && (
              <Badge variant="info" className="text-xs">Home & Home</Badge>
            )}
          </div>

          {/* Date & Time */}
          <p className="text-brand-charcoal font-semibold text-sm mb-1.5">
            {formatDate(posting.date)} • {formatTime(posting.start_time)}
            {posting.end_time && ` - ${formatTime(posting.end_time)}`}
          </p>

          {/* Location (for available postings) */}
          {posting.posting_type === 'available' && (
            <p className="text-brand-slate text-xs leading-snug mb-1.5">
              {posting.rink_name && (
                <>
                  <span className="font-medium">{posting.rink_name}</span> • 
                </>
              )}
              {posting.city}, {posting.state}
              {posting.rink_address && ` • ${posting.rink_address}`}
            </p>
          )}

          {/* Wanted location hint */}
          {posting.posting_type === 'wanted' && (
            <p className="text-brand-slate text-xs mb-1.5">
              Looking for scrimmage near {posting.city}, {posting.state}
            </p>
          )}

          {/* Referee Info + Distance */}
          <div className="flex items-center gap-2 flex-wrap">
            {posting.referee_type && posting.referee_type !== 'none' && (
              <p className="text-brand-slate text-xs">
                {posting.referee_type === 'coach_led' ? '👥 Coach-led scrimmage' : '👔 Paid referees'}
                {posting.scrimmage_fee && ` - $${posting.scrimmage_fee} split`}
              </p>
            )}
            {distance_miles && (
              <Badge variant="default" className="text-xs">
                {distance_miles} mi
              </Badge>
            )}
          </div>

          {/* Notes */}
          {posting.notes && (
            <p className="text-brand-slate text-xs mt-2 italic leading-snug">
              "{posting.notes}"
            </p>
          )}

          {/* Calendar Events for this date (mobile) - show YOUR or THEIR schedule depending on perspective */}
          {(your_calendar_events !== undefined || their_calendar_events !== undefined) && (
            <div className="mt-3 p-2 bg-gray-50 rounded border">
              <div className="text-xs font-medium text-gray-700 mb-1">
                📋 {match_perspective === 'supply' 
                  ? `${other_team_name || 'Their'}'s Schedule` 
                  : 'Your Schedule'} on {formatDate(posting.date)}:
              </div>
              {(() => {
                const events = match_perspective === 'supply' ? their_calendar_events : your_calendar_events
                return events && events.length > 0 ? (
                  <div className="space-y-1">
                    {events.map((evt, idx) => (
                      <div key={idx} className="text-xs flex items-center gap-1.5 flex-wrap">
                        <span className={`px-1.5 py-0.5 rounded ${
                          evt.event_type === 'game' ? 'bg-red-100 text-red-700' :
                          evt.event_type === 'practice' ? 'bg-blue-100 text-blue-700' :
                          evt.event_type === 'tournament' ? 'bg-purple-100 text-purple-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {evt.event_type}
                        </span>
                        <span className="text-gray-600">
                          {new Date(evt.start_time).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                        </span>
                        <span className="text-gray-500 truncate">{evt.title}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-xs text-gray-500 italic">
                    No events synced
                  </div>
                )
              })()}
            </div>
          )}

          {/* Association Website Link for seed teams (mobile) */}
          {isSeedTeam && team_info.association_website_url && (
            <a
              href={team_info.association_website_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-2 text-xs text-brand-slate hover:text-brand-charcoal"
            >
              <span>🔗</span>
              <span>Visit Association</span>
            </a>
          )}

          {/* Claim Team CTA for unclaimed seed teams (mobile) */}
          {isSeedTeam && !team_info.claimed_at && (
            <Link 
              to={`/claim-team/${posting.team_id}`}
              className="inline-flex items-center gap-1 mt-2 text-xs text-brand-green hover:text-green-700 hover:underline"
            >
              <span>🏒</span>
              <span>Is this your team?</span>
            </Link>
          )}
        </div>
      </Card>

      {showContact && (contactEmail || contactPhone) && (
        <ContactScrimmageModal
          teamName={team_info.name}
          managerInfo={manager_info}
          posting={posting}
          onClose={() => setShowContact(false)}
        />
      )}
    </>
  )
}

